import React, { useEffect, useState, useCallback } from 'react';
import LinkHubWidget from '../../components/kidsa/LinkHubWidget';


const modalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .modal-content {
    animation: modalSlideIn 0.3s ease-out;
  }
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-50px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
    }
    50% {
      box-shadow: 0 4px 20px rgba(102, 126, 234, 0.8);
    }
    100% {
      box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
    }
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-3px);
    }
    60% {
      transform: translateY(-1px);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.3);
    }
  }

  .badge-fadeout {
    animation: fadeOut 0.3s ease-out forwards;
  }

  .notification-button-dragging {
    cursor: grabbing !important;
    transform: scale(1.1) !important;
    box-shadow: 0 8px 30px rgba(102, 126, 234, 0.7) !important;
    z-index: 1200 !important;
  }

  @media (max-width: 768px) {
    .responsive-table {
      font-size: 12px;
    }
    .responsive-table th,
    .responsive-table td {
      padding: 6px !important;
      word-break: break-word;
    }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('modal-custom-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'modal-custom-styles';
  styleSheet.textContent = modalStyles;
  document.head.appendChild(styleSheet);
}

if (typeof document !== 'undefined' && !document.getElementById('mammoth-script')) {
  const mammothScript = document.createElement('script');
  mammothScript.id = 'mammoth-script';
  mammothScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.4.2/mammoth.browser.min.js';
  mammothScript.async = true;
  document.head.appendChild(mammothScript);
}

// Interfaces
interface MenuItem {
  text: string;
  image: string;
  url: string;
  active?: boolean;
  subMenu?: MenuItem[];
}

interface ContactInfo {
  address: string;
  email: string;
  phone?: string;
}

interface SocialLinks {
  facebook: string;
  linkedin: string;
  youtube: string;
}

interface CategoryOption {
  value: string;
  label: string;
}

interface ChatConfig {
  apiUrl?: string;
  apiKey?: string;
  botName?: string;
  welcomeMessage?: string;
  enabled?: boolean;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  buttonSize?: 'small' | 'medium' | 'large';
  theme?: 'light' | 'dark';
}

interface ModalConfig {
  enabled: boolean;
  title: string;
  message: string;
  documentPath?: string;
  logo?: string;
  showDate?: boolean;
  showSignature?: boolean;
  signatureText?: string[];
  autoShow?: boolean;
  showOnLoad?: boolean;
  startDate?: string;
  endDate?: string;
}

interface KidsaHeaderProps {
  logo?: string;
  contactInfo: ContactInfo;
  socialLinks: SocialLinks;
  menuItems: MenuItem[];
  ctaButton: {
    text: string;
    url: string;
    image?: string;
  };
  categories?: CategoryOption[];
  chatConfig?: ChatConfig;
  modalConfig?: ModalConfig;
}

const KidsaHeader: React.FC<KidsaHeaderProps> = ({
  contactInfo,
  logo,
  ctaButton,
  menuItems,
  socialLinks,
  chatConfig,
  modalConfig
}) => {
  const [sticky, setSticky] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [documentContent, setDocumentContent] = useState<string>('');
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [notificationSeen, setNotificationSeen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [badgeFading, setBadgeFading] = useState(false);
  
  const [buttonPosition, setButtonPosition] = useState(() => {
    return {
      x: 0,
      y: 0
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  const isModalDateActive = (startDate?: string, endDate?: string): boolean => {
  if (!startDate && !endDate) return true; // Si no hay fechas definidas, siempre activa
  
  const now = new Date();
  const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Solo fecha, sin hora
  
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return currentDate >= start && currentDate <= end;
  }
  
  if (startDate) {
    const start = new Date(startDate);
    return currentDate >= start;
  }
  
  if (endDate) {
    const end = new Date(endDate);
    return currentDate <= end;
  }
  
  return true;
};

  // Memoized scroll handler
  const handleScroll = useCallback(() => {
    setSticky(window.scrollY > 100);
  }, []);

  // Add and remove scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const initializePosition = () => {
      const isMobile = window.innerWidth < 768;
      const initialPosition = {
        x: isMobile ? window.innerWidth - 75 : window.innerWidth - 80,
        y: isMobile ? window.innerHeight - 140 : window.innerHeight - 154 
      };
      
      setButtonPosition(initialPosition);
      setIsInitialized(true);
    };

    const timer = setTimeout(initializePosition, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      
      if (!isChatOpen) {
        setButtonPosition({
          x: isMobile ? window.innerWidth - 75 : window.innerWidth - 80,
          y: isMobile ? window.innerHeight - 140 : window.innerHeight - 154 
        });
      } else {
        setButtonPosition(prev => ({
          x: Math.min(prev.x, window.innerWidth - 80),
          y: Math.min(prev.y, window.innerHeight - 80)
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isChatOpen, isInitialized]);

  useEffect(() => {
    if (isChatOpen) {
      const isMobile = window.innerWidth < 768;
      const chatOffset = isMobile ? 380 : 450; 
      const newY = window.innerHeight - chatOffset;
      
      setButtonPosition(prev => ({
        x: isMobile ? Math.max(20, prev.x - 100) : Math.max(20, prev.x - 150), 
        y: Math.max(20, newY) 
      }));
    } else {
      const isMobile = window.innerWidth < 768;
      const defaultY = isMobile ? window.innerHeight - 140 : window.innerHeight - 160;
      
      setButtonPosition(prev => ({
        x: prev.x < 100 ? (isMobile ? window.innerWidth - 75 : window.innerWidth - 80) : prev.x, 
        y: prev.y < defaultY * 0.7 ? defaultY : prev.y 
      }));
    }
  }, [isChatOpen]);

  useEffect(() => {
    const checkChatState = () => {
      try {
        const findElementWithText = (text: string) => {
          const walker = document.createTreeWalker(
            document.body,
            NodeFilter.SHOW_TEXT,
            null
          );
          
          let node;
          while (node = walker.nextNode()) {
            if (node.textContent?.includes(text)) {
              return node.parentElement;
            }
          }
          return null;
        };

        const chatIndicators = [
          document.querySelector('[data-chat-open="true"]'),
          document.querySelector('[data-widget-open="true"]'),
          document.querySelector('.chat-widget-open'),
          document.querySelector('.widget-open'),
          document.querySelector('[aria-expanded="true"]'),
          
          document.querySelector('form[style*="display: block"]'),
          document.querySelector('div[style*="display: block"] form'),
          
          findElementWithText("Para Comenzar, Completa Tus Datos"),
          findElementWithText("Comenzar Chat"),
          findElementWithText("Completa tus datos"),
          findElementWithText("AnubisAI"),
          
          document.querySelector('input[placeholder*="Juan"]'),
          document.querySelector('input[placeholder*="Pérez"]'),
          document.querySelector('input[placeholder*="email"]'),
          document.querySelector('input[placeholder*="teléfono"]'),
          document.querySelector('input[placeholder*="+569"]'),
          
          document.querySelector('.chat-container.expanded'),
          document.querySelector('.chat-form-visible'),
          document.querySelector('.chat-expanded'),
          
          ...Array.from(document.querySelectorAll('button')).filter(btn => 
            btn.textContent?.includes('Comenzar Chat') || 
            btn.textContent?.includes('Enviar') ||
            btn.style.backgroundColor.includes('rgb(220, 53, 69)') ||
            btn.style.backgroundColor.includes('#dc3545') ||
            btn.style.backgroundColor.includes('rgb(255, 15, 0)') ||
            btn.style.backgroundColor.includes('#ff0f00')
          )
        ];
        
        const highZIndexElements = Array.from(document.querySelectorAll('div')).filter(el => {
          const styles = window.getComputedStyle(el);
          const rect = el.getBoundingClientRect();
          const zIndex = parseInt(styles.zIndex) || 0;
          
          return zIndex > 1000 && 
                 rect.width > 250 && 
                 rect.height > 250 &&
                 rect.bottom > window.innerHeight * 0.3 &&
                 rect.right > window.innerWidth * 0.3; 
        });
        
        const isOpen = [...chatIndicators, ...highZIndexElements].some(indicator => indicator !== null);
        setIsChatOpen(isOpen);
        
      } catch (error) {
        console.warn('Error detecting chat state:', error);
      }
    };

    const observer = new MutationObserver(() => {
      setTimeout(checkChatState, 50);
      setTimeout(checkChatState, 200);
      setTimeout(checkChatState, 500);
    });
        observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-chat-open', 'aria-expanded', 'data-widget-open']
    });

    checkChatState();
    const interval = setInterval(checkChatState, 2000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

 useEffect(() => {
    if (showModal && modalConfig?.documentPath && !documentContent) {
      loadDocumentContent(modalConfig.documentPath);
    }
  }, [showModal, modalConfig?.documentPath, documentContent]);

useEffect(() => {
  const hasShownModal = sessionStorage.getItem('modalShown');
  const isMainPage = window.location.pathname === '/elcristo' && !window.location.hash;
  
  if (isMainPage && !hasShownModal) {
    if (modalConfig?.enabled && 
        modalConfig?.showOnLoad && 
        isModalDateActive(modalConfig?.startDate, modalConfig?.endDate)) {
      
      setShowModal(true);
      sessionStorage.setItem('modalShown', 'true');
      
      if (modalConfig?.documentPath && !documentContent) {
        loadDocumentContent(modalConfig.documentPath);
      }
    }
  }
}, [modalConfig, documentContent]);

  const handleStart = useCallback((clientX: number, clientY: number, target: HTMLElement) => {
    setIsDragging(true);
    
    const rect = target.getBoundingClientRect();
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY, e.currentTarget as HTMLElement);
  }, [handleStart]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY, e.currentTarget as HTMLElement);
  }, [handleStart]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging) return;
    
    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;
    
    const buttonSize = window.innerWidth < 768 ? 55 : 60;
    const maxX = window.innerWidth - buttonSize;
    const maxY = window.innerHeight - buttonSize;
    
    setButtonPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging, dragOffset]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  }, [handleMove]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      
      document.body.style.userSelect = 'none';
      (document.body.style as any).webkitUserSelect = 'none';
      document.body.style.touchAction = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEnd);
        
        document.body.style.userSelect = '';
        (document.body.style as any).webkitUserSelect = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isDragging, handleMouseMove, handleTouchMove, handleEnd]);

  const loadDocumentContent = async (filePath: string) => {
    setIsLoadingDocument(true);
    try {
      const fileExtension = filePath.split('.').pop()?.toLowerCase() || '';
      
      switch (fileExtension) {
        case 'txt':
          await loadTextFile(filePath);
          break;
        case 'pdf':
          await loadPdfFile(filePath);
          break;
        case 'docx':
          await loadDocxFile(filePath);
          break;
        case 'xlsx':
        case 'xls':
          await loadExcelFile(filePath);
          break;
        case 'pptx':
          await loadPowerPointFile(filePath);
          break;
        case 'csv':
          await loadCsvFile(filePath);
          break;
        default:
          await loadGenericFile(filePath, fileExtension);
      }
    } catch (error) {
      setDocumentContent(`
        <div style="text-align: center; padding: 30px; color: #e74c3c;">
          <div style="font-size: 48px; margin-bottom: 15px;">⚠️</div>
          <h4 style="color: #e74c3c; margin-bottom: 10px;">Error al cargar el documento</h4>
          <p style="color: #7f8c8d;">No se pudo procesar el archivo. Verifica que la ruta sea correcta.</p>
        </div>
      `);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const loadTextFile = async (filePath: string) => {
    try {
      const response = await fetch(filePath);
      const text = await response.text();
      const formattedText = text
        .replace(/\n/g, '<br>')
        .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
      setDocumentContent(`
        <div style="padding: 20px; font-family: 'Courier New', monospace; 
                    white-space: pre-wrap; line-height: 1.6; background: white; border-radius: 8px;">
          ${formattedText}
        </div>
      `);
    } catch (error) {
      setDocumentContent(`
        <div style="text-align: center; padding: 30px; color: #e74c3c;">
          <div style="font-size: 32px; margin-bottom: 10px;">❌</div>
          <p>Error al cargar el archivo de texto</p>
        </div>
      `);
    }
  };

  const loadDocxFile = async (filePath: string) => {
    try {
      const response = await fetch(filePath);
      
      if (!response.ok) {
        throw new Error(`Error al cargar el archivo: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      const mammoth = (window as any).mammoth;
      if (!mammoth) {
        setDocumentContent(`
          <div style="padding: 20px; text-align: center;">
            <div style="font-size: 48px; margin-bottom: 15px;">📝</div>
            <h5 style="color: #2196F3; margin-bottom: 10px;">Documento Microsoft Word</h5>
            <p style="color: #666; margin-bottom: 20px;">
              El documento está listo para descargar. Haz clic en "Descargar" para visualizarlo.
            </p>
            <div style="background: #e3f2fd; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <small style="color: #1565c0;">
                <strong>💡 Tip:</strong> Los documentos Word mantienen su formato original al abrirse
              </small>
            </div>
            
            <button 
              onclick="window.open('${filePath}', '_blank')"
              style="
                background-color: #2196F3;
                border: none;
                color: white;
                padding: 12px 24px;
                border-radius: 8px;
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                gap: 8px;
                transition: all 0.3s ease;
                text-decoration: none;
              "
              onmouseover="this.style.backgroundColor='#1976D2'; this.style.transform='translateY(-1px)'"
              onmouseout="this.style.backgroundColor='#2196F3'; this.style.transform='translateY(0)'"
            >
              <i class="fas fa-download"></i>
              Descargar Documento
            </button>
          </div>
        `);
        return;
      }
      
      const result = await mammoth.convertToHtml({arrayBuffer});
      const html = result.value;
      const messages = result.messages;
      
      let warningsHtml = '';
      if (messages && messages.length > 0) {
        const warnings = messages.filter((m: any) => m.type === 'warning');
        if (warnings.length > 0) {
          warningsHtml = `
            <div style="background: #fff3cd; padding: 10px; border-radius: 6px; margin-bottom: 15px; border-left: 4px solid #ffc107;">
              <small style="color: #856404;">
                <strong>⚠️ Nota:</strong> Algunos elementos del documento pueden no visualizarse completamente.
              </small>
            </div>
          `;
        }
      }
      
      setDocumentContent(`
        <div style="padding: 20px; background: white; border-radius: 8px;">
          <div style="text-align: center; margin-bottom: 20px; 
                      padding: 15px; background: #e3f2fd; border-radius: 8px;">
            <div style="font-size: 32px; margin-bottom: 8px;">📝</div>
            <strong style="color: #1976D2;">Vista Previa del Documento Word</strong>
          </div>
          
          ${warningsHtml}
          
          <div style="
            border: 1px solid #e0e0e0; 
            border-radius: 8px; 
            padding: 20px; 
            background: white;
            max-height: 400px;
            overflow-y: auto;
            font-family: 'Times New Roman', serif;
            line-height: 1.6;
            color: #333;
          ">
            ${html}
          </div>
          
          <div style="text-align: center; margin-top: 20px;">
            <button 
              onclick="window.open('${filePath}', '_blank')"
              style="
                background-color: #2196F3;
                border: none;
                color: white;
                padding: 10px 20px;
                border-radius: 6px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                gap: 6px;
                transition: all 0.3s ease;
                text-decoration: none;
              "
              onmouseover="this.style.backgroundColor='#1976D2'; this.style.transform='translateY(-1px)'"
              onmouseout="this.style.backgroundColor='#2196F3'; this.style.transform='translateY(0)'"
            >
              <i class="fas fa-external-link-alt"></i>
              Ver Documento Completo
            </button>
          </div>
          
          <div style="background: #e8f5e8; padding: 12px; border-radius: 6px; margin-top: 15px;">
            <small style="color: #2e7d32;">
              <strong>✅ Éxito:</strong> Documento cargado y convertido para vista previa
            </small>
          </div>
        </div>
      `);
      
    } catch (error) {
      console.error('Error loading DOCX file:', error);
      setDocumentContent(`
        <div style="padding: 20px; text-align: center;">
          <div style="font-size: 48px; margin-bottom: 15px;">📝</div>
          <h5 style="color: #f44336; margin-bottom: 10px;">Error al cargar vista previa</h5>
          <p style="color: #666; margin-bottom: 20px;">
            No se pudo procesar el documento para vista previa. Puedes descargarlo directamente.
          </p>
          
          <button 
            onclick="window.open('${filePath}', '_blank')"
            style="
              background-color: #2196F3;
              border: none;
              color: white;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              transition: all 0.3s ease;
              text-decoration: none;
            "
            onmouseover="this.style.backgroundColor='#1976D2'; this.style.transform='translateY(-1px)'"
            onmouseout="this.style.backgroundColor='#2196F3'; this.style.transform='translateY(0)'"
          >
            <i class="fas fa-download"></i>
            Descargar Documento
          </button>
          
          <div style="background: #ffebee; padding: 12px; border-radius: 6px; margin-top: 15px;">
            <small style="color: #c62828;">
              <strong>❌ Error:</strong> ${error instanceof Error ? error.message : 'Error desconocido'}
            </small>
          </div>
        </div>
      `);
    }
  };

  const loadExcelFile = async (filePath: string) => {
    setDocumentContent(`
      <div style="padding: 20px; text-align: center;">
        <div style="font-size: 48px; margin-bottom: 15px;">📊</div>
        <h5 style="color: #10793F; margin-bottom: 10px;">Hoja de Cálculo Excel</h5>
        <p style="color: #666; margin-bottom: 15px;">
          Vista previa de Excel no disponible. El documento se abrirá en una nueva pestaña.
        </p>
        <div style="background: #e8f5e8; padding: 15px; border-radius: 8px;">
          <small style="color: #2e7d32;">
            <strong>📈 Tip:</strong> Los archivos Excel incluyen fórmulas, gráficos y hojas adicionales
          </small>
        </div>
      </div>
    `);
  };

  const loadPowerPointFile = async (filePath: string) => {
    setDocumentContent(`
      <div style="padding: 20px; text-align: center;">
        <div style="font-size: 48px; margin-bottom: 15px;">📋</div>
        <h5 style="color: #D04423; margin-bottom: 10px;">Presentación PowerPoint</h5>
        <p style="color: #666; margin-bottom: 15px;">
          Vista previa de PowerPoint no disponible. El documento se abrirá en una nueva pestaña.
        </p>
        <div style="background: #fff3cd; padding: 15px; border-radius: 8px;">
          <small style="color: #856404;">
            <strong>🎯 Tip:</strong> Las presentaciones incluyen animaciones y contenido multimedia
          </small>
        </div>
      </div>
    `);
  };

  const loadCsvFile = async (filePath: string) => {
    try {
      const response = await fetch(filePath);
      const text = await response.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        setDocumentContent(`
          <div style="text-align: center; padding: 30px; color: #e74c3c;">
            <div style="font-size: 32px; margin-bottom: 10px;">📋</div>
            <p>Archivo CSV vacío</p>
          </div>
        `);
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const maxRows = Math.min(lines.length - 1, 8);
      
      let tableHtml = `
        <div style="padding: 20px; background: white; border-radius: 8px;">
          <div style="text-align: center; margin-bottom: 20px; 
                      padding: 15px; background: #e8f5e8; border-radius: 8px;">
            <div style="font-size: 32px; margin-bottom: 8px;">📋</div>
            <strong style="color: #2e7d32;">Datos CSV - ${lines.length - 1} registros</strong>
          </div>
          
          <div style="overflow-x: auto; margin-bottom: 15px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
              <thead>
                <tr style="background: #4CAF50; color: white;">
      `;
      
      headers.forEach(header => {
        tableHtml += `<th style="padding: 8px; border: 1px solid #ddd; font-weight: 600;">${header}</th>`;
      });
      tableHtml += '</tr></thead><tbody>';
      
      for (let i = 1; i <= maxRows; i++) {
        if (lines[i] && lines[i].trim()) {
          const cells = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
          const bgColor = i % 2 === 0 ? '#f9f9f9' : 'white';
          tableHtml += `<tr style="background: ${bgColor};">`;
          cells.forEach(cell => {
            tableHtml += `<td style="padding: 6px; border: 1px solid #e0e0e0;">${cell}</td>`;
          });
          tableHtml += '</tr>';
        }
      }
      
      if (lines.length > 9) {
        tableHtml += `
          <tr style="background: #e3f2fd;">
            <td colspan="${headers.length}" style="padding: 10px; text-align: center; color: #1565c0;">
              ... y ${lines.length - 9} registros más
            </td>
          </tr>
        `;
      }
      
      tableHtml += `
            </tbody>
          </table>
        </div>
        
        <div style="background: #e3f2fd; padding: 12px; border-radius: 6px;">
          <small style="color: #1565c0;">
            <strong>📊 Resumen:</strong> ${headers.length} columnas • ${lines.length - 1} filas totales
          </small>
        </div>
      </div>
      `;
      
      setDocumentContent(tableHtml);
    } catch (error) {
      setDocumentContent(`
        <div style="text-align: center; padding: 30px; color: #e74c3c;">
          <div style="font-size: 32px; margin-bottom: 10px;">❌</div>
          <p>Error al cargar archivo CSV</p>
        </div>
      `);
    }
  };

  const loadPdfFile = async (filePath: string) => {
    setDocumentContent(`
      <div style="padding: 20px; background: white; border-radius: 8px;">
        <div style="text-align: center; margin-bottom: 20px; 
                    padding: 15px; background: #ffebee; border-radius: 8px;">
          <div style="font-size: 32px; margin-bottom: 8px;">📄</div>
          <strong style="color: #c62828;">Documento PDF</strong>
        </div>
        
        <!-- Visor PDF embebido -->
        <div style="border: 2px solid #ddd; border-radius: 8px; background: #fafafa; margin-bottom: 20px;">
          <iframe 
            src="${filePath}#toolbar=1&navpanes=1&scrollbar=1&page=1&zoom=auto"
            style="
              width: 100%; 
              height: 400px; 
              border: none; 
              border-radius: 6px;
            "
            title="Vista previa del PDF"
            onload="this.style.background='white'"
            onerror="this.style.display='none'; this.nextElementSibling.style.display='block'"
          >
          </iframe>
          
          <!-- Fallback si iframe no funciona -->
          <div style="display: none; padding: 30px; text-align: center;">
            <div style="font-size: 48px; margin-bottom: 15px; opacity: 0.7;">📖</div>
            <p style="color: #666; margin-bottom: 15px;">
              Vista previa no disponible en este navegador
            </p>
            <small style="color: #999;">
              Haz clic en "Ver PDF Completo" para abrir el documento
            </small>
          </div>
        </div>
        
        <div style="text-align: center;">
          <button 
            onclick="window.open('${filePath}', '_blank')"
            style="
              background-color: #c62828;
              border: none;
              color: white;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              transition: all 0.3s ease;
              text-decoration: none;
            "
            onmouseover="this.style.backgroundColor='#b71c1c'; this.style.transform='translateY(-1px)'"
            onmouseout="this.style.backgroundColor='#c62828'; this.style.transform='translateY(0)'"
          >
            <i class="fas fa-external-link-alt"></i>
            Ver PDF Completo
          </button>
        </div>
        
        <div style="background: #e8eaf6; padding: 12px; border-radius: 6px; margin-top: 15px;">
          <small style="color: #3f51b5;">
            <strong>💡 Sugerencia:</strong> Para mejor experiencia, abre el PDF en una nueva pestaña
          </small>
        </div>
      </div>
    `);
  };

  const loadGenericFile = async (filePath: string, extension: string) => {
    const displayExtension = extension || 'Desconocido';
    setDocumentContent(`
      <div style="text-align: center; padding: 40px;">
        <div style="background: linear-gradient(135deg, #78909C 0%, #546E7A 100%); 
                    border-radius: 15px; padding: 30px; color: white; margin-bottom: 20px;">
          <div style="font-size: 64px; margin-bottom: 15px;">📎</div>
          <h4 style="margin-bottom: 10px;">Archivo ${displayExtension.toUpperCase()}</h4>
          <p style="margin-bottom: 20px; opacity: 0.9;">
            Este tipo de archivo no es compatible con la vista previa.
          </p>
          <div style="background: rgba(255,255,255,0.2); 
                      border-radius: 8px; padding: 15px; margin-top: 15px;">
            <small>💡 <strong>Sugerencia:</strong> Haz clic en "Ver Documento" para descargarlo</small>
          </div>
        </div>
      </div>
    `);
  };

  const renderSubMenu = (items: MenuItem[]) => {
    return (
      <ul className="submenu">
        {items.map((item, index) => (
          <li 
            key={index} 
            className={item.subMenu ? "has-dropdown" : ""}
          >
            <a href={item.url}>
              {item.text}
              {item.subMenu && <i className="fas fa-angle-down"></i>}
            </a>
            {item.subMenu && renderSubMenu(item.subMenu)}
          </li>
        ))}
      </ul>
    );
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleSearch = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setShowSearch(!showSearch);
  };

  const toggleModal = () => {
    if (!isDragging) {
      setShowModal(!showModal);
      if (!showModal && !notificationSeen) {
        setBadgeFading(true);
        setTimeout(() => {
          setNotificationSeen(true);
          setBadgeFading(false);
        }, 300);
      }
    }
  };

  const openDocument = () => {
    if (modalConfig?.documentPath) {
      window.open(modalConfig.documentPath, '_blank');
    }
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {/* Top Header Section */}
      <div className="header-top-section">
        <div className="header-top-shape">
          <img src="/assets/kidsa/assets/img/header-top-shape-4.png" alt="shape-img" />
        </div>
        <div className="container-fluid">
          <div className="header-top-wrapper">
            <ul className="contact-list">
              <li>
                <i className="fal fa-map-marker-alt"></i>
                {contactInfo.address}
              </li>
              <li>
                <i className="far fa-envelope"></i>
                <a href={`mailto:${contactInfo.email}`} className="link">
                  {contactInfo.email}
                </a>
              </li>
            </ul>
            <div className="social-icon d-flex align-items-center mb-3">
              <span>Síguenos en:</span>
              {Object.entries(socialLinks).map(([platform, url]) => (
                <a target="_blank" key={platform} href={url}>
                  <i className={`fab fa-${platform}`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Header */}
      <header 
        id="header-sticky" 
        className={`header-1 mb-0 ${sticky ? 'sticky' : ''} ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}
      >
        <div className="container-fluid">
          <div className="mega-menu-wrapper">
            <div className="header-main style-2">
              <div className="header-left">
                <div className="logo">
                  <a href="./">
                    <img 
                      src={logo} 
                      alt="logo-img" 
                    />
                  </a>
                </div>
              </div>
              <div className="header-right d-flex justify-content-end align-items-center">
                <div className={`mean__menu-wrapper ${mobileMenuOpen ? 'active' : ''}`}>
                  <div className="main-menu">
                    <nav id="mobile-menu">
                      <ul>
                        {menuItems.map((item, index) => (
                          <li 
                            key={index} 
                            className={`
                              ${item.subMenu ? "has-dropdown" : ""} 
                              ${item.active ? "active" : ""} 
                              ${item.subMenu && item.subMenu.length > 4 ? "menu-thumb" : ""}
                            `}
                          >
                            <a href={item.url}>
                              {item.text}
                              {item.subMenu && <i className="fas fa-angle-down"></i>}
                            </a>
                            {item.subMenu && renderSubMenu(item.subMenu)}
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </div>

                <div className="header-button">
                  <a href={ctaButton.url} className="themes-btn">
                    <span>
                      <img src={ctaButton.image} width="60" />
                    </span>
                  </a>
                </div>
                <div 
                  className="header__hamburger d-xl-none my-auto" 
                  onClick={toggleMobileMenu}
                >
                  <div className="sidebar__toggle">
                    <i className="fas fa-bars"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Search Overlay */}
      {showSearch && (
        <div className="search-wrap">
          <div className="search-inner">
            <i 
              className="fas fa-times search-close" 
              id="search-close"
              onClick={toggleSearch}
            ></i>
            <div className="search-cell">
              <div className="search-field-holder">
                <input 
                  type="search" 
                  className="main-search-input" 
                  placeholder="Buscar..." 
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Comunicado - RESPONSIVE */}
      {modalConfig?.enabled && showModal && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'flex', 
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(2px)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1055,
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => e.target === e.currentTarget && toggleModal()}
        >
          <div 
            className="modal-dialog"
            style={{
              margin: window.innerWidth < 768 ? '10px' : '20px auto',
              maxWidth: window.innerWidth < 768 ? 'calc(100vw - 20px)' : '800px',
              width: '100%',
              position: 'relative'
            }}
          >
            <div 
              className="modal-content"
              style={{
                borderRadius: '15px',
                border: 'none',
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                maxHeight: window.innerWidth < 768 ? 'calc(90vh - 20px)' : 'calc(90vh - 40px)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Header elegante - CORREGIDO PARA MÓVIL */}
              <div 
                className="modal-header" 
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: window.innerWidth < 768 ? '15px' : '25px 30px',
                  color: 'white',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '10px'
                }}
              >
                {/* Contenido principal del header */}
                <div 
                  className="d-flex align-items-center"
                  style={{ 
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {modalConfig.logo && (
                    <div 
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        padding: window.innerWidth < 768 ? '6px' : '8px',
                        marginRight: window.innerWidth < 768 ? '10px' : '20px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        flexShrink: 0
                      }}
                    >
                      <img 
                        src={modalConfig.logo} 
                        alt="Logo" 
                        style={{ 
                          width: window.innerWidth < 768 ? '40px' : '65px', 
                          height: window.innerWidth < 768 ? '40px' : '65px',
                          objectFit: 'contain',
                          filter: 'brightness(1.1)'
                        }} 
                      />
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h4 
                      className="modal-title"
                      style={{ 
                        color: 'white', 
                        fontWeight: '600',
                        fontSize: window.innerWidth < 768 ? '18px' : '28px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        margin: 0,
                        marginBottom: window.innerWidth < 768 ? '5px' : '8px',
                        wordBreak: 'break-word'
                      }}
                    >
                      {modalConfig.title}
                    </h4>
                    <div className="d-flex align-items-center">
                      <div 
                        style={{
                          width: window.innerWidth < 768 ? '25px' : '40px',
                          height: '3px',
                          backgroundColor: 'rgba(255,255,255,0.8)',
                          borderRadius: '2px',
                          marginRight: '8px',
                          flexShrink: 0
                        }}
                      ></div>
                      <small style={{ 
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: window.innerWidth < 768 ? '11px' : '14px',
                        fontWeight: '500'
                      }}>
                        Colegio El Cristo
                      </small>
                    </div>
                  </div>
                </div>

                {/* Botón de cerrar - POSICIÓN CORREGIDA */}
                <button 
                  type="button" 
                  onClick={toggleModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: window.innerWidth < 768 ? '18px' : '24px',
                    padding: '8px',
                    borderRadius: '50%',
                    width: window.innerWidth < 768 ? '35px' : '45px',
                    height: window.innerWidth < 768 ? '35px' : '45px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    flexShrink: 0,
                    alignSelf: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ×
                </button>
              </div>
              
              <div 
                className="modal-body" 
                style={{ 
                  overflowY: 'auto',
                  padding: window.innerWidth < 768 ? '15px' : '30px',
                  backgroundColor: '#fafbfc',
                  flex: 1
                }}
              >
                {/* Mensaje principal con formato HTML - Responsive */}
                <div className="mb-4">
                  <div 
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: window.innerWidth < 768 ? '15px' : '25px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <div 
                      dangerouslySetInnerHTML={{ __html: modalConfig.message }}
                      style={{ 
                        fontSize: window.innerWidth < 768 ? '14px' : '16px', 
                        lineHeight: '1.7',
                        color: '#2c3e50'
                      }}
                      className="responsive-table"
                    />
                  </div>
                </div>

                {/* Fecha con diseño mejorado - Responsive */}
                {modalConfig.showDate && (
                  <div className="mb-4" style={{ textAlign: window.innerWidth < 768 ? 'center' : 'right' }}>
                    <div 
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        padding: window.innerWidth < 768 ? '6px 12px' : '8px 16px',
                        borderRadius: '20px',
                        fontSize: window.innerWidth < 768 ? '12px' : '14px',
                        fontWeight: '500'
                      }}
                    >
                      <i className="fas fa-calendar-alt" style={{ marginRight: '8px' }}></i>
                      {getCurrentDate()}
                    </div>
                  </div>
                )}

                {/* Documento adjunto con diseño premium - Responsive */}
                {modalConfig.documentPath && (
                  <div className="document-preview mb-4">
                    <div 
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: window.innerWidth < 768 ? '15px' : '20px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        border: '1px solid #e9ecef'
                      }}
                    >
                      <div 
                        style={{
                          display: 'flex',
                          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                          justifyContent: 'space-between',
                          alignItems: window.innerWidth < 768 ? 'stretch' : 'center',
                          marginBottom: '15px',
                          gap: window.innerWidth < 768 ? '10px' : '0'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div 
                            style={{
                              backgroundColor: '#4285f4',
                              borderRadius: '8px',
                              padding: window.innerWidth < 768 ? '6px' : '8px',
                              marginRight: '12px',
                              flexShrink: 0
                            }}
                          >
                            <i className="fas fa-file-alt" style={{ color: 'white', fontSize: window.innerWidth < 768 ? '14px' : '16px' }}></i>
                          </div>
                          <h6 style={{ 
                            color: '#2c3e50', 
                            marginBottom: 0, 
                            fontWeight: '600',
                            fontSize: window.innerWidth < 768 ? '14px' : '16px'
                          }}>
                            Documento Adjunto
                          </h6>
                        </div>
                      </div>
                      
                      {/* Vista previa del documento - Responsive */}
                      <div 
                        className="document-content"
                        style={{
                          border: '1px solid #e1e5e9',
                          borderRadius: '10px',
                          backgroundColor: '#f8f9fa',
                          maxHeight: window.innerWidth < 768 ? '250px' : '350px',
                          overflowY: 'auto'
                        }}
                      >
                        {isLoadingDocument ? (
                          <div className="text-center py-4">
                            <div 
                              style={{
                                width: window.innerWidth < 768 ? '30px' : '40px',
                                height: window.innerWidth < 768 ? '30px' : '40px',
                                border: '4px solid #e3f2fd',
                                borderTop: '4px solid #4285f4',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 15px'
                              }}
                            ></div>
                            <p className="mb-0" style={{ 
                              color: '#6c757d',
                              fontSize: window.innerWidth < 768 ? '12px' : '14px'
                            }}>
                              Cargando documento...
                            </p>
                          </div>
                        ) : (
                          <div 
                            dangerouslySetInnerHTML={{ __html: documentContent }}
                            style={{ 
                              padding: '0',
                              color: '#495057'
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Firma elegante - Responsive */}
                {modalConfig.showSignature && modalConfig.signatureText && (
                  <div 
                    className="signature-section preserve-newlines mt-4"
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: window.innerWidth < 768 ? '15px' : '20px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <div style={{ textAlign: window.innerWidth < 768 ? 'center' : 'right' }}>
                      <p className="mb-0" style={{ 
                        fontStyle: 'italic',
                        color: '#6c757d',
                        fontSize: window.innerWidth < 768 ? '13px' : '15px',
                        lineHeight: '1.5',
                        fontWeight: '500'
                      }}>
                        {modalConfig.signatureText}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer con botones mejorados - Responsive */}
              <div 
                className="modal-footer" 
                style={{ 
                  borderTop: '1px solid #e9ecef',
                  backgroundColor: '#fafbfc',
                  padding: window.innerWidth < 768 ? '15px' : '20px 30px',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                  gap: window.innerWidth < 768 ? '10px' : '12px'
                }}
              >
                <button 
                  type="button" 
                  onClick={toggleModal}
                  style={{
                    backgroundColor: '#6c757d',
                    border: 'none',
                    color: 'white',
                    borderRadius: '8px',
                    padding: window.innerWidth < 768 ? '12px 20px' : '10px 20px',
                    fontSize: window.innerWidth < 768 ? '16px' : '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    width: window.innerWidth < 768 ? '100%' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#5a6268';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6c757d';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón flotante de comunicados - ARRASTRABLE y con posición dinámica */}
      {modalConfig?.enabled && 
        isInitialized && 
          isModalDateActive(modalConfig?.startDate, modalConfig?.endDate) && (
        <div 
          style={{
            position: 'fixed',
            left: `${buttonPosition.x}px`,
            top: `${buttonPosition.y}px`,
            zIndex: isDragging ? 1200 : (isChatOpen ? 999 : 1000), 
            cursor: isDragging ? 'grabbing' : 'grab',
            transition: isDragging ? 'none' : 'all 0.3s ease',
            opacity: isInitialized ? 1 : 0 
          }}
        >
          <button
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onClick={toggleModal}
            className={isDragging ? 'notification-button-dragging' : ''}
            style={{
              width: window.innerWidth < 768 ? '55px' : '60px',
              height: window.innerWidth < 768 ? '55px' : '60px',
              borderRadius: '50%',
              border: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontSize: window.innerWidth < 768 ? '18px' : '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDragging 
                ? '0 8px 30px rgba(102, 126, 234, 0.7)' 
                : '0 4px 20px rgba(102, 126, 234, 0.4)',
              transition: isDragging ? 'none' : 'all 0.3s ease',
              animation: isDragging ? 'none' : 'pulse 2s infinite',
              transform: isDragging ? 'scale(1.1)' : 'scale(1)',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitTapHighlightColor: 'transparent'
            } as React.CSSProperties}
            onMouseEnter={(e) => {
              if (!isDragging) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 6px 25px rgba(102, 126, 234, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDragging) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
          >
            <i className="fas fa-bullhorn"></i>
          </button>
          
          {!notificationSeen && !badgeFading && (
            <div
              className={badgeFading ? 'badge-fadeout' : ''}
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#ff4757',
                color: 'white',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                border: '2px solid white',
                animation: badgeFading ? 'fadeOut 0.3s ease-out forwards' : 'bounce 1s infinite',
                pointerEvents: 'none'
              }}
            >
              1
            </div>
          )}
        </div>
      )}

      {/* LinkHUB Widget - Nuevo chat modernizado */}
      <LinkHubWidget
        apiKey="tDpYn6E3G6OhZIA8eip2El1sYAy2qkS0TaWqi0QcJAg" 
        wsUrl="wss://backend.anubisai.net/ws/widgets/elcristo" 
      />
    </>
  );
};

export default KidsaHeader;